import React from 'react';
import WordHeaderArticle from './WordHeaderArticle';
import WordHeaderCase from './WordHeaderCase';
import WordHeaderPreposition from './WordHeaderPreposition';
import WordHeaderReflexive from './WordHeaderReflexive';
import WordHeaderSlot from './WordHeaderSlot';
import NounWithEnding from './NounWithEnding';
import { getHeaderLineItems } from '../utils/wordDisplayUtils';

const WordHeaderLine = ({
  word,
  includeExamples = true,
  includeArticle = true,
  showGerman = true,
  germanClassName = '',
  germanStyle,
  onGermanDoubleClick,
  onGermanClick,
  otherClassName = '',
  className = '',
}) => {
  const items = getHeaderLineItems(word, {
    includeExamples,
    includeArticle,
    includeGerman: showGerman,
  });

  const renderExample = (example) => {
    if (!example) return null;
    return (
      <span className={`italic text-[var(--ink-500)] ${otherClassName}`}>
        ({example})
      </span>
    );
  };

  // Headword template segments inside the German text: plain words, object
  // slots ("jmdm."), governed prepositions and an inline "sich".
  const renderGermanSegments = (segments) => segments.map((seg, i) => {
    const space = i > 0 ? ' ' : '';
    if (seg.type === 'text') return <React.Fragment key={`seg-${i}`}>{space}{seg.text}</React.Fragment>;
    if (seg.tokenType === 'slot') {
      return (
        <React.Fragment key={`seg-${i}`}>
          {space}
          <WordHeaderSlot abbreviation={seg.abbreviation} caseValue={seg.caseValue} />
          {seg.example ? <> {renderExample(seg.example)}</> : null}
        </React.Fragment>
      );
    }
    if (seg.tokenType === 'preposition') {
      return <React.Fragment key={`seg-${i}`}>{space}{seg.preposition}</React.Fragment>;
    }
    if (seg.tokenType === 'reflexive') {
      return (
        <React.Fragment key={`seg-${i}`}>
          {space}
          <WordHeaderReflexive caseValue={seg.caseValue} />
        </React.Fragment>
      );
    }
    return null;
  });

  return (
    <div className={`flex items-center gap-1 flex-wrap ${className}`}>
      {items.map((item, index) => {
        switch (item.type) {
          case 'article':
            return (
              <WordHeaderArticle
                key={`header-${index}`}
                article={item.article}
                gender={item.gender}
                className={otherClassName}
              />
            );
          case 'german': {
            const type = word?.type || '';
            const isNoun = type === 'Noun' || type === 'Noun with Preposition';
            const wordGender = word?.grammar_info?.gender;
            const feminineForm = isNoun ? (word?.grammar_info?.feminine_form || '').trim() : '';
            return (
              <span
                key={`header-${index}`}
                className={`inline-flex flex-wrap items-baseline gap-1 ${germanClassName}`}
                style={germanStyle}
                onDoubleClick={onGermanDoubleClick}
                onClick={onGermanClick}
              >
                <span>
                  {item.segments ? (
                    renderGermanSegments(item.segments)
                  ) : isNoun ? (
                    <NounWithEnding word={item.text} gender={wordGender} />
                  ) : (
                    item.text
                  )}
                </span>
                {feminineForm ? (
                  <span className={`text-[var(--ink-500)] ${otherClassName}`}>
                    ({feminineForm})
                  </span>
                ) : null}
              </span>
            );
          }
          case 'reflexive':
            return (
              <WordHeaderReflexive
                key={`header-${index}`}
                caseValue={item.caseValue}
                className={otherClassName}
              />
            );
          case 'object_case':
            return (
              <span key={`header-${index}`} className="flex items-center gap-1">
                <WordHeaderCase caseValue={item.caseValue} className={otherClassName} />
                {renderExample(item.example)}
              </span>
            );
          case 'slot':
            return (
              <span key={`header-${index}`} className="flex items-center gap-1">
                <WordHeaderSlot
                  abbreviation={item.abbreviation}
                  caseValue={item.caseValue}
                  className={otherClassName}
                />
                {renderExample(item.example)}
              </span>
            );
          case 'object_label':
            return (
              <span key={`header-${index}`} className={otherClassName}>
                {item.label}
              </span>
            );
          case 'preposition':
            return (
              <span key={`header-${index}`} className="flex items-center gap-1">
                <WordHeaderPreposition preposition={item.preposition} className={otherClassName} />
                {renderExample(item.example)}
              </span>
            );
          case 'preposition_case':
            return (
              <span key={`header-${index}`} className="flex items-center gap-1">
                <WordHeaderCase caseValue={item.caseValue} className={otherClassName} />
                {renderExample(item.example)}
              </span>
            );
          case 'preposition_example':
            return (
              <span key={`header-${index}`} className={otherClassName}>
                {renderExample(item.example)}
              </span>
            );
          default:
            return null;
        }
      })}
    </div>
  );
};

export default WordHeaderLine;
